import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { executeCapability } from '../../utils/capabilityExecute'
import type { JobTreadJobOption } from './types'
import { normalizeJobOption } from './types'
import { useAutoDismiss } from './useAutoDismiss'
import { isInsideKioskVirtualKeyboard } from '../../utils/kioskVirtualKeyboard'

type JobSearchFieldProps = {
  label?: string
  value: JobTreadJobOption | null
  onChange: (job: JobTreadJobOption | null) => void
  required?: boolean
  placeholder?: string
  disabled?: boolean
  /** Prefer tally inventory.searchJobs; falls back to forms JobTread search. */
  capabilityApp?: 'tally' | 'forms'
}

function jobDisplay(job: JobTreadJobOption): string {
  if (job.number && job.name) return `${job.number} — ${job.name}`
  return job.name || job.number || job.id || ''
}

export const JobSearchField: React.FC<JobSearchFieldProps> = ({
  label = 'Location',
  value,
  onChange,
  required = false,
  placeholder = 'Search JobTread locations...',
  disabled = false,
  capabilityApp = 'tally',
}) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<JobTreadJobOption[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  useAutoDismiss(error, () => setError(null))

  useEffect(() => {
    if (value) setQuery(jobDisplay(value))
  }, [value])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (isInsideKioskVirtualKeyboard(e.target)) return
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const search = useCallback(
    async (q: string) => {
      const trimmed = q.trim()
      if (trimmed.length < 2) {
        setResults([])
        setLoading(false)
        setSearched(false)
        setOpen(trimmed.length > 0)
        return
      }
      const requestId = ++requestIdRef.current
      setLoading(true)
      setError(null)
      setOpen(true)
      setSearched(false)
      try {
        const capability =
          capabilityApp === 'tally' ? 'inventory.searchJobs' : 'jobtread.searchJobs'
        const data = await executeCapability<{ results: JobTreadJobOption[] }>({
          app: capabilityApp,
          capability,
          input: { q: trimmed },
        })
        if (requestId !== requestIdRef.current) return
        setResults(
          (Array.isArray(data?.results) ? data.results : [])
            .slice(0, 20)
            .map((job) => normalizeJobOption(job)),
        )
        setSearched(true)
        setOpen(true)
      } catch (err: unknown) {
        if (requestId !== requestIdRef.current) return
        setResults([])
        setSearched(true)
        setError(err instanceof Error ? err.message : 'Failed to search jobs')
      } finally {
        if (requestId === requestIdRef.current) setLoading(false)
      }
    },
    [capabilityApp],
  )

  const handleQueryChange = (next: string) => {
    setQuery(next)
    if (value) onChange(null)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const trimmed = next.trim()
    if (trimmed.length > 0 && trimmed.length < 2) {
      setLoading(false)
      setSearched(false)
      setResults([])
      setOpen(true)
      setError(null)
      return
    }
    if (!trimmed) {
      setLoading(false)
      setSearched(false)
      setResults([])
      setOpen(false)
      setError(null)
      return
    }
    setLoading(true)
    setOpen(true)
    debounceRef.current = setTimeout(() => {
      void search(next)
    }, 280)
  }

  const selectJob = (job: JobTreadJobOption) => {
    const normalized = normalizeJobOption(job)
    onChange(normalized)
    setQuery(jobDisplay(normalized))
    setOpen(false)
    setResults([])
    setSearched(false)
  }

  const clear = () => {
    onChange(null)
    setQuery('')
    setResults([])
    setOpen(false)
    setSearched(false)
    setError(null)
    setLoading(false)
  }

  const showDropdown = open && !disabled && !value
  const queryLen = query.trim().length

  return (
    <div className="w-full" ref={wrapRef}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          {label}
          {required ? <span className="text-error"> *</span> : null}
        </span>
        {value ? (
          <button type="button" className="text-xs link link-hover" onClick={clear} disabled={disabled}>
            Clear
          </button>
        ) : null}
      </div>
      <div className="relative">
        <input
          className={`input input-bordered w-full pr-10 ${loading ? 'border-primary/40' : ''}`}
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          onFocus={() => {
            if (!value && query.trim()) setOpen(true)
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          required={required && !value}
          aria-busy={loading}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/45 pointer-events-none">
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" />
          ) : (
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          )}
        </span>

        {showDropdown ? (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-base-content/70">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" />
                Searching JobTread…
              </div>
            ) : queryLen > 0 && queryLen < 2 ? (
              <div className="px-3 py-3 text-sm text-base-content/60">Type at least 2 characters to search</div>
            ) : error ? (
              <div className="px-3 py-3 text-sm text-error">{error}</div>
            ) : searched && results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-base-content/60">No open locations match “{query.trim()}”</div>
            ) : results.length > 0 ? (
              <ul className="max-h-56 overflow-y-auto">
                {results.map((job) => (
                  <li key={String(job.id || job.number || job.name)}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-base-200"
                      onClick={() => selectJob(job)}
                    >
                      <div className="font-medium">{jobDisplay(job)}</div>
                      {job.location?.address ? (
                        <div className="text-xs text-base-content/60 truncate">{job.location.address}</div>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-error">{error}</p> : null}
      {loading && !showDropdown ? (
        <p className="mt-1 text-xs text-primary flex items-center gap-1.5">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          Searching JobTread…
        </p>
      ) : null}
      {value ? (
        <p className="mt-1 text-xs text-base-content/60">
          Selected{value.number ? ` #${value.number}` : ''}
          {value.id ? ` · id ${value.id}` : ''}
        </p>
      ) : null}
    </div>
  )
}

export default JobSearchField
